import { useEffect, useRef, useState } from 'react'

import classnames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import HorizontalRule from '~/components/atoms/HorizontalRule/HorizontalRule'
import Text from '~/components/atoms/Text/Text'
import { TextSizes } from '~/components/atoms/Text/Text'
import { BannerTheme } from '~/enums/banner-theme'

import styles from './AnimatedMessageBanner.module.css'

type Slide = {
  title: string
  text: string
}

export interface AnimatedMessageBannerProps {
  testID?: string
  slides: Slide[]
  speed?: number
  theme?: BannerTheme
}

type ActiveSlide = {
  title: string
  text: string
  ref: number
}

export default function AnimatedMessageBanner({
  testID,
  slides,
  speed = 5000,
  theme = BannerTheme.dark,
}: AnimatedMessageBannerProps) {
  const [activeSlideDetails, setActiveSlideDetails] = useState<ActiveSlide>(
    slides && {
      ...slides[0],
      ref: 0,
    }
  )

  const slider = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateScrollPosition = () => {
      if (slider) {
        const sliderWidth = slider.current?.clientWidth
        if (sliderWidth) {
          slider.current?.scrollTo({
            left: sliderWidth * activeSlideDetails.ref,
          })
        }
      }
    }
    const interval = setInterval(() => {
      let nextSlide = activeSlideDetails.ref + 1
      if (nextSlide >= slides.length) nextSlide = 0
      setActiveSlide(nextSlide)
    }, speed)

    updateScrollPosition()

    window.addEventListener('resize', updateScrollPosition)

    return () => {
      clearInterval(interval)
      window.removeEventListener('resize', updateScrollPosition)
    }
  }, [activeSlideDetails])

  const setActiveSlide = (key: number) => {
    setActiveSlideDetails({
      ...slides[key],
      ref: key,
    })
  }

  return (
    <div
      className={classnames(styles[theme], styles.base)}
      data-testid={testID}
    >
      <Container>
        <div className={styles.slider} ref={slider}>
          {slides.map((slide, key) => (
            <div
              className={classnames(
                styles.content,
                activeSlideDetails.ref === key && styles.contentActive,
                styles.contentTitle
              )}
              key={key}
            >
              <div>
                <Text
                  Tag="h2"
                  size={TextSizes.primaryTitleLrger}
                  className={styles.title}
                >
                  <span dangerouslySetInnerHTML={{ __html: slide.title }} />
                </Text>
                <Grid>
                  <Row>
                    <Col variant="col-full-width col-1/2--md">
                      <span></span>
                    </Col>
                  </Row>
                  <Row>
                    <Col variant="col-full-width">
                      <HorizontalRule />
                    </Col>
                  </Row>
                  <Row>
                    <Col variant="col-full-width col-2/3--md">
                      <span></span>
                    </Col>
                    <Col variant="col-full-width col-1/3--md">
                      <Text
                        Tag="p"
                        size={TextSizes.secondaryTitleLrg}
                        className={styles.body}
                      >
                        {slide.text}
                      </Text>
                    </Col>
                  </Row>
                </Grid>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </div>
  )
}
